import { FC } from "react";
import styled from "@emotion/styled";
import { MDSModalDialog } from "@/mds/components/overlays/dialog";
import { MDSDialogSlotName } from "@/mds/components/overlays/dialog/dialog.types";
import { MDSText, MDSTextVariant, MDSTextWeight } from "@/mds/components/text";
import { MDSButton, MDSButtonVariant } from "@/mds/components/buttons/button";
import { STEP_CONFIGS, type StepId } from "@/store/getting-started";

export interface GettingStartedHelpModalProps {
  isOpen: boolean;
  stepId: StepId;
  onClose: () => void;
}

export const GettingStartedHelpModal: FC<GettingStartedHelpModalProps> = ({
  isOpen,
  stepId,
  onClose,
}) => {
  // Find the step config and get its help content
  const stepConfig = STEP_CONFIGS.find(config => config.id === stepId);
  const content = stepConfig?.helpContent;

  if (!content) {
    return null;
  }

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={onClose} isDismissable={true}>
      <StyledHeader slot={MDSDialogSlotName.Header}>
        <MDSText variant={MDSTextVariant.HeaderL} weight={MDSTextWeight.SemiBold}>
          {content.title}
        </MDSText>
      </StyledHeader>

      <StyledContent slot={MDSDialogSlotName.Content}>
        <MDSText variant={MDSTextVariant.ParagraphSm}>{content.description}</MDSText>
        <StyledImageContainer>
          <StyledImage src={content.image} alt={content.title} />
        </StyledImageContainer>
      </StyledContent>

      <StyledFooter slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton variant={MDSButtonVariant.Contrast} onPress={onClose}>
          Got it
        </MDSButton>
      </StyledFooter>
    </MDSModalDialog>
  );
};

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["4"]};
  width: 100%;
`;

const StyledImageContainer = styled.div`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  overflow: hidden;
`;

const StyledImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const StyledFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing["2"]};
`;
